
  import { Component, Prop } from 'vue-property-decorator'
  import { GForm } from '@/components/forms/GForm'
  import { ValidationStatus, FormValidationItem, FormValidationSection } from '@/entities/entity'

@Component
  export default class FormStepper extends GForm {
  @Prop({ type: Array, default: () => [] }) value!: FormValidationItem[]
  @Prop({ type: String, default: '' }) title!: string

  mainPanel = 0 // El panel principal estará expandido por defecto
  ValidationStatus = ValidationStatus

  /**
   * Devuelve el ícono según el estado del paso
   */
  getIcon (val: ValidationStatus) {
    switch (val) {
      case ValidationStatus.OK:
        return 'mdi-check-circle'
      case ValidationStatus.NEED_CORRECTION:
        return 'mdi-alert-circle'
      case ValidationStatus.OPTIONAL:
        return 'mdi-information'
      default:
        return 'mdi-alert-circle-outline'
    }
  }

  /**
   * Devuelve el color del ícono según el estado del paso
   */
  getColor (val: ValidationStatus) {
    switch (val) {
      case ValidationStatus.OK:
        return 'green'
      case ValidationStatus.NEED_CORRECTION:
        return 'red'
      case ValidationStatus.OPTIONAL:
        return 'blue'
      default:
        return 'orange'
    }
  }

  /**
   * Devuelve la leyenda del tooltip según el estado del paso
   */
  getTooltipText (val: ValidationStatus): string {
    switch (val) {
      case ValidationStatus.OK:
        return 'Ok'
      case ValidationStatus.NEED_CORRECTION:
        return 'Por corrección'
      case ValidationStatus.OPTIONAL:
        return 'Opcional'
      default:
        return 'Pendiente'
    }
  }

  isLink (link: string | string[]) {
    if (!link) return false

    if (typeof link === 'string') return true

    if (Array.isArray(link) && link.length) return true
  }

  /**
   * Devuelve el estado de validación general
   */
  get getAllTrueStatus (): ValidationStatus {
    const nonOptionalValidations = this.validations.filter(v => v.value !== ValidationStatus.OPTIONAL)
    const allOk = nonOptionalValidations.every(val => val.value === ValidationStatus.OK)
    const hasCorrection = nonOptionalValidations.some(val => val.value === ValidationStatus.NEED_CORRECTION)
    const hasPending = nonOptionalValidations.some(val => val.value === ValidationStatus.PENDING)

    if (allOk) return ValidationStatus.OK
    if (hasCorrection) return ValidationStatus.NEED_CORRECTION
    if (hasPending) return ValidationStatus.PENDING
    return ValidationStatus.PENDING
  }

  /**
   * Obtiene el estado de validación de una sección específica
   */
  getSectionStatus (section: FormValidationSection): ValidationStatus {
    const nonOptionalItems = section.items.filter(item => item.value !== ValidationStatus.OPTIONAL)
    const allOk = nonOptionalItems.every(item => item.value === ValidationStatus.OK)
    const hasCorrection = nonOptionalItems.some(item => item.value === ValidationStatus.NEED_CORRECTION)
    const hasPending = nonOptionalItems.some(item => item.value === ValidationStatus.PENDING)

    if (allOk) return ValidationStatus.OK
    if (hasCorrection) return ValidationStatus.NEED_CORRECTION
    if (hasPending) return ValidationStatus.PENDING
    return ValidationStatus.PENDING
  }

  /**
   * Calcula el progreso de una sección específica
   */
  getSectionProgress (section: FormValidationSection) {
    const nonOptionalItems = section.items.filter(item => item.value !== ValidationStatus.OPTIONAL)
    const completedItems = section.items.filter(item => item.value === ValidationStatus.OK)
    const total = nonOptionalItems.length
    const completed = completedItems.length
    const percentage = total > 0 ? (completed / total) * 100 : 0

    return {
      total,
      completed,
      percentage,
    }
  }

  /**
   * Obtiene la lista de validaciones desde las props
   */
  get validations () {
    if (!this.value?.length) return []
    return this.value
  }

  /**
   * Agrupa las validaciones por sección
   */
  get sections (): FormValidationSection[] {
    if (!this.validations.length) return []

    // Agrupar por sección
    const groupedBySection = this.validations.reduce((acc, item) => {
      const sectionName = item.section || 'General'
      if (!acc[sectionName]) {
        acc[sectionName] = []
      }
      acc[sectionName].push(item)
      return acc
    }, {} as Record<string, FormValidationItem[]>)

    // Convertir a array de secciones y ordenar
    return Object.entries(groupedBySection)
      .map(([name, items]) => {
        // Calcular el orden de la sección basado en el order mínimo de sus campos
        const minOrder = Math.min(...items.map(item => item.order))
        return {
          name,
          order: minOrder,
          items: items.sort((a, b) => a.order - b.order),
        }
      })
      .sort((a, b) => a.order - b.order)
  }

  /**
   * Obtiene el color según el porcentaje de progreso
   */
  getProgressColor (progress: number): string {
    switch (true) {
      case (progress >= 0 && progress < 6.4):
        return 'grey'
      case (progress >= 6.6 && progress < 35):
        return 'red'
      case (progress >= 35 && progress < 50):
        return 'orange'
      case (progress >= 50 && progress < 72):
        return 'yellow'
      case (progress >= 72 && progress < 93):
        return 'green'
      default:
        return 'primary'
    }
  }

  /**
   * Verifica si el texto es demasiado largo y necesita ser truncado
   */
  isTextLong (text: string): boolean {
    // Consideramos texto largo si tiene más de 35 caracteres
    return text && text.length > 35
  }
  }
